/**
 * The four performance meters (Deal security, Client satisfaction, Counterparty cooperation,
 * Transaction process). Each shows its label + numeric value + a fill bar; the value text is always
 * present so meaning never depends on colour alone. Wrapped in an aria-live region so changes are
 * announced politely after a decision.
 */
import type { Dimension } from "@patc/content-schema";
import type { Scores } from "../game/scoring";
import { cx } from "../lib/ui";

/** Bar colour bands — reinforced by the always-visible numeric value, never colour-only. */
function meterTone(pct: number): string {
  if (pct >= 66) return "bg-[var(--success)]";
  if (pct >= 45) return "bg-[var(--color-tier-good)]";
  if (pct >= 30) return "bg-[var(--warning)]";
  return "bg-[var(--danger)]";
}

export function ScoreMeter({ dim, value }: { dim: Dimension; value: number }) {
  const pct = Math.round(((value - dim.min) / (dim.max - dim.min)) * 100);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-ink-700">{dim.label}</span>
        <span className="font-mono text-xs tabular-nums text-ink-600">{value}</span>
      </div>
      <div
        role="meter"
        aria-valuenow={value}
        aria-valuemin={dim.min}
        aria-valuemax={dim.max}
        aria-label={dim.label}
        className="h-1.5 w-full overflow-hidden rounded-full bg-ink-150"
      >
        <div
          className={cx("h-full rounded-full transition-[width] duration-500 ease-out", meterTone(pct))}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function Meters({ dimensions, scores }: { dimensions: Dimension[]; scores: Scores }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4" aria-live="polite">
      {dimensions.map((d) => (
        <ScoreMeter key={d.id} dim={d} value={scores[d.id]} />
      ))}
    </div>
  );
}
